import { useEffect, useMemo, useState } from "react";
import { Chart } from "../../../../common/components/Chart/Chart";
import { DropdownMenu } from "../../../../common/components/DropdownMenu";
import FullModal from "../../../../common/components/FullModal";
import { SearchElement, SearchForm, SearchLabel, SearchRow } from "../../../../common/components/Search/Search";
import { downloadExcel } from "../../../../common/functions/downloadExcel";
import { piezometriService } from "../../../../services/PiezometriService";
import { Table } from "../../components/micro/Table";
import { Tr } from "../../components/micro/Tr";
import { targetValue } from "../../../../common/functions/targetValue";

/**
 * @typedef LivelloPiezometricoPageProps
 * @prop {string} idPiezometro
 */

/**
 * @typedef MeasurementSearchParams
 * @prop {Date} from
 * @prop {Date} to
 * @prop {string} type
 * @prop {string} levelType
 */

/**
 * @typedef Measure
 * @prop {string} date
 * @prop {string} level
 * @prop {string} type
 * @prop {string} levelType
 */

const api = {
    /**
     * @param {string} idPiezometro
     * @param {MeasurementSearchParams} params
     * @returns {Promise<Measure[]>}
     */
    search: async (idPiezometro, params) => {
        return (await piezometriService.getLevels(idPiezometro, params.from, params.to, params.type, params.levelType))
            .map(toMeasure)
    },

    /**
     *
     * @param {string} idPiezometro
     * @returns {Promise<Measure>}
     */
    last: async idPiezometro => toMeasure(
        await piezometriService.lastLevels(idPiezometro)
    )

}

/**
 * @typedef {import("../../../../services/PiezometriService").Level} Level
 * @param {Level} level
 * @returns {Measure}
 */
const toMeasure = level => level == null ? null : ({
    date: level.date.toLocaleDateString(),
    level: level.value,
    levelType: level.TIPOLOGIA_MEDICION,
    type: level.NE_ND,
})

/**
 * @param {LivelloPiezometricoPageProps} param0
 */
export function LivelloPiezometricoPage({idPiezometro, nomePiezometro}) {

    const [lastMeasurement, setLastMeasurement] = useState()

    useEffect(() => {
        api.last(idPiezometro).then(setLastMeasurement)
    }, [idPiezometro])

    useEffect(() => {
        setFilteredData([])
    }, [idPiezometro])

    const [loading, setLoading] = useState(false)

    /**
     * @type {[Measure[], (m: Measure[]) => void]>}
     */
    const [filteredData, setFilteredData] = useState([])

    const chartData = useMemo(() => filteredData.map(d => [
        new Date(d.date).getTime(),
        parseFloat(d.level.replace(',', '.')),
    ]), [filteredData])

    const onSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()
        switch (getAction(e)) {
            case 'search':
                setSearched(true)
                doSearch(e)
                break
            case 'clear':
                setSearched(false)
                e.target.reset()
                setFilteredData([])
                break
            default: return
        }
    }

    const doSearch = e => {
        if (validateParams(e)) {
            setLoading(true)
            return api.search(idPiezometro, getSearchParams(e))
                .then(setFilteredData)
                .then(() => setLoading(false))
        }
    }

    const getAction = e => e.nativeEvent.submitter.name

    const getSearchParams = e => {
        const rawParams = Object.fromEntries(
            ['from', 'to', 'type', 'levelType']
                .map(el => [el, e.target.elements[el].value])
                .map(x => (console.log(x), x))
                .filter(([_k, v]) => Boolean(v))
        )

        return {
            ...rawParams,
            from: new Date(rawParams.from === '' ? 0 : rawParams.from),
            to: new Date(rawParams.to === '' ? '3000-12-31' : rawParams.to)
        }
    }

    const validateParams = e => true
        // ['type', 'levelType'].every(el => Boolean(e.target.elements[el].value))
        //     ? e
        //     : null

    const [showChart, setShowChart] = useState(false)
    const onChart = e => {
        e.preventDefault()
        setShowChart(true)
    }
    const onExport = e => {
        e.preventDefault()
        xlsxExport('Piezometro ' + nomePiezometro, filteredData)
    }

    const [searched, setSearched] = useState(false)

    return (
        <>
            <SamplesTable
                title={<th colSpan='100%'>Ultima misura{ lastMeasurement ? '' : ' non disponibile'}</th>}
                samples={lastMeasurement ? [lastMeasurement] : []}
                showDate={true}
                showHeaders={lastMeasurement != null}
            />

            <SearchForm className="p-3" onSubmit={onSubmit}>
                <SearchRow>
                    <SearchElement>
                        <SearchLabel>Da:&nbsp;</SearchLabel>
                        <input id="from" name="from" type="date"/>
                    </SearchElement>

                    <SearchElement>
                        <SearchLabel>A:&nbsp;</SearchLabel>
                        <input id="to" name="to" type="date"/>
                    </SearchElement>
                </SearchRow>

                <SearchRow>
                    <SearchElement>
                        <SearchLabel>Misura:&nbsp;</SearchLabel>
                        <select id="type" name="type">
                            <option>Automatica</option>
                            <option>Manuale</option>
                        </select>
                    </SearchElement>

                    <SearchElement>
                        <SearchLabel>Livello:&nbsp;</SearchLabel>
                        <select id="levelType" name="levelType">
                            <option>Statico</option>
                            <option>Dinamico</option>
                            <option>N.D.</option>
                        </select>
                    </SearchElement>

                    <div className="d-flex align-items-end justify-content-end">
                        <button type="submit" name="search" id="search-btn" className="btn btn-sm"><i className="fa fa-search text-white"></i></button>
                        <button type="submit" name="clear" id="clear-btn" className="btn btn-sm"><i className="fa fa-eraser text-white"></i></button>
                        <DropdownMenu
                            className="text-dark"
                            trigger={<i className="fa fa-bars text-white"></i>}
                            items={[
                                {text: 'Grafico', onClick: onChart},
                                {text: 'Esporta', onClick: onExport}
                            ]}
                        />
                    </div>
                </SearchRow>
            </SearchForm>

            {loading
                ? <div className="text-center my-5">RICERCA IN CORSO...</div>
                : <SamplesTable
                    samples={filteredData}
                    showDate={true}
                    noResults= {searched
                        ? 'Nessun risultato'
                        : 'Effettua una ricerca per visualizzare dei dati'
                    }
                  />
            }

            {showChart &&
                <FullModal onClose={() => setShowChart(false)}>
                    <Chart
                        title={nomePiezometro}
                        subtitle='Piezometro'
                        name='Livello Piezometrico'
                        yTitle='m'
                        data={chartData}
                    />
                </FullModal>
            }

        </>
    )
}

/**
 * @typedef {SamplesTableProps}
 * @prop {Measure[]} samples
 * @prop {boolean} showDate
 * @prop {ReactNode | undefined} title
 * @prop {ReactNode | undefined} noResults
 * @prop {boolean} showResults
 */

/**
 * @param {SamplesTableProps} param0
 * @returns
 */
function SamplesTable({
    samples,
    showDate,
    title,
    noResults,
    showHeaders = true,
}) {
    return (
        <Table className="samples-table">
            <thead>
                {title && <Tr className="title">{title}</Tr>}
                {showHeaders &&
                    <Tr>
                        <th>Livello piezometrico [m]</th>
                        <th>Tipo di misura</th>
                        <th>Tipo di livello</th>
                        {showDate && <th>Data</th>}
                    </Tr>
                }
            </thead>

            <tbody>
                {samples.length === 0 && noResults &&
                    <Tr><td className="text-center" colSpan="4">{noResults}</td></Tr>
                }
                {samples.map((sample, i) =>
                    <Tr key={i}>
                        <td>{sample.level}</td>
                        <td>{sample.type === "ND" ? "Automatica" : "Manuale"}</td>
                        <td>{sample.levelType === "M" ? "Statico" : "Dinamico"}</td>
                        {showDate && <td>{sample.date}</td>}
                    </Tr>

                )}
            </tbody>
        </Table>
    )
}
/**
 * @param {string} title
 * @param {Measure[]} samples
 */
const xlsxExport = (title, samples) => {

    const data = [
        ['Livello piezometrico [m]', 'Tipo di misura', 'Tipo di livello', 'Data'],
        ...samples.map(sample => [
            sample.level,
            sample.type === 'ND' ? 'Automatica' : 'Manuale',
            sample.levelType === 'M' ? 'Statico' : 'Dinamico',
            sample.date
        ])
    ]

    downloadExcel(title, data);
}